import * as React from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Alert,
    Stack,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { deleteQuizConfiguration, getQuizConfigurations, getQuizConfigurationsError } from '../Api';
import { QuizConfigurationDto } from '../models/dtos/QuizAdminDtos';

export default function QuizConfigurationsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [items, setItems] = React.useState<QuizConfigurationDto[]>([]);
    const [totalItems, setTotalItems] = React.useState(0);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(25);
    const [deleteId, setDeleteId] = React.useState<number | null>(null);
    const [loadError, setLoadError] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        const result = await getQuizConfigurations(page * pageSize, pageSize);
        if (result == null) {
            setItems([]);
            setTotalItems(0);
            const errDetail = await getQuizConfigurationsError(page * pageSize, pageSize);
            setLoadError(
                errDetail ??
                    'Could not load from API. Deploy betup-server-analytics with /api/dashboard/quiz and check login.'
            );
        } else {
            setItems(result.items ?? []);
            setTotalItems(result.totalItems ?? 0);
        }
        setLoading(false);
    }, [page, pageSize]);

    React.useEffect(() => {
        load();
    }, [load]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        {
            field: 'sport',
            headerName: 'Sport',
            flex: 1,
            valueGetter: (params: GridValueGetterParams) => params.row.sport?.name ?? '—',
        },
        {
            field: 'leagueIds',
            headerName: 'Leagues',
            width: 120,
            valueGetter: (params: GridValueGetterParams) => params.row.leagueIds?.length ?? 0,
        },
        {
            field: 'betGroupIds',
            headerName: 'Bet groups',
            width: 120,
            valueGetter: (params: GridValueGetterParams) => params.row.betGroupIds?.length ?? 0,
        },
        {
            field: 'deadlineInHoursBeforeMatchStart',
            headerName: 'Deadline (h)',
            width: 120,
        },
        {
            field: 'isActive',
            headerName: 'Active',
            width: 100,
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={params.value ? 'Active' : 'Inactive'}
                    color={params.value ? 'success' : 'default'}
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 200,
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => navigate(`/quiz/configurations/${params.row.id}/edit`)}>
                        Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => setDeleteId(params.row.id)}>
                        Delete
                    </Button>
                </Stack>
            ),
        },
    ];

    const confirmDelete = async () => {
        if (deleteId == null) return;
        await deleteQuizConfiguration(deleteId);
        setDeleteId(null);
        load();
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">Quiz sport &amp; leagues</Typography>
                <Button variant="contained" onClick={() => navigate('/quiz/configurations/create')}>
                    Add configuration
                </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Rows in <strong>qbet_quiz_configuration</strong> — same table cron reads. App quizzes unchanged.
            </Typography>
            {loadError != null && (
                <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
            )}
            {loading ? (
                <CircularProgress />
            ) : (
                <DataGrid
                    autoHeight
                    rows={items}
                    columns={columns}
                    getRowId={(row) => row.id}
                    paginationMode="server"
                    rowCount={totalItems}
                    paginationModel={{ page, pageSize }}
                    onPaginationModelChange={(model) => {
                        setPage(model.page);
                        setPageSize(model.pageSize);
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                />
            )}
            <Dialog open={deleteId != null} onClose={() => setDeleteId(null)}>
                <DialogTitle>Delete configuration?</DialogTitle>
                <DialogContent>This will stop new quizzes from being generated for this config.</DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteId(null)}>Cancel</Button>
                    <Button color="error" onClick={confirmDelete}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
